import { useState } from 'react'
import { CurrencyDropdown } from './CurrencyDropdown'
import { CurrencyRateInput } from './CurrencyRateInput'
import { formatDisplayDate } from '@/lib/dateUtils'
import { setInvoiceActiveCurrency } from '@/services/currencyRateService'
import { showErrorToast } from '@/hooks/use-toast'
import type { Invoice } from '../types'

interface RedesignedCurrencyFieldProps {
  invoice: Invoice
  onInvoiceRefresh: () => Promise<void>
  readOnly?: boolean
}

export const RedesignedCurrencyField = ({
  invoice,
  onInvoiceRefresh,
  readOnly,
}: RedesignedCurrencyFieldProps): JSX.Element => {
  const [isSwitching, setIsSwitching] = useState(false)

  const currencyCode = invoice.active_currency_code || ''
  const activeRepresentation = invoice.currency_representations?.find(
    (r) => r.currency_code === currencyCode
  )
  const currencyRateInfo = invoice.currency_rate_info ?? null
  // Prefer the actual rate date from the provider (may differ from invoice date on weekends)
  const rateDate = currencyRateInfo?.rate_date
    ?? invoice.extractedData?.find((f) => f.field_name === 'invoice_date')?.field_value

  const handleCurrencyChange = async (value: string) => {
    if (value === '__clear') {
      return
    }

    const targetCurrency = value.toUpperCase()
    setIsSwitching(true)
    try {
      await setInvoiceActiveCurrency(invoice.id, targetCurrency)
      // Refresh invoice to get updated representations and amounts
      await onInvoiceRefresh()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to switch currency'
      showErrorToast(message)
    } finally {
      setIsSwitching(false)
    }
  }

  const sourceBadge = activeRepresentation ? (
    <span
      className={`ml-2 inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold ${
        activeRepresentation.source === 'extracted'
          ? 'border-green-200 bg-green-100 text-green-700'
          : 'border-blue-200 bg-blue-100 text-blue-700'
      }`}
    >
      {activeRepresentation.source === 'extracted' ? 'Extracted' : 'Calculated'}
    </span>
  ) : null

  const showRate = currencyCode && currencyCode !== 'EUR'

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 items-start gap-4">
        <div className="space-y-1 min-w-0">
          <label className="block text-sm font-normal text-neutral-500 leading-5 2xl:text-xs">
            Currency
          </label>
          <div className="flex items-center min-w-0">
            <div className="min-w-0 flex-1">
              <CurrencyDropdown
                value={currencyCode}
                onChange={handleCurrencyChange}
                disabled={isSwitching || readOnly}
              />
            </div>
            {sourceBadge}
            {isSwitching && (
              <span className="ml-2 text-xs text-neutral-400">Switching...</span>
            )}
          </div>
        </div>

        {showRate && (
          <div className="space-y-1 min-w-0">
            <label className="block text-sm font-normal text-neutral-500 leading-5 2xl:text-xs">
              Exchange Rate
            </label>
            <div className="flex min-h-9 items-center">
              <CurrencyRateInput
                invoice={invoice}
                currencyRateInfo={currencyRateInfo}
                onInvoiceRefresh={onInvoiceRefresh}
                currencyCode={currencyCode}
                readOnly={readOnly}
              />
            </div>
          </div>
        )}
      </div>

      {rateDate && (
        <div className="text-xs text-neutral-500">
          Rate date: {formatDisplayDate(rateDate)}
        </div>
      )}

      {activeRepresentation?.source === 'calculated' && activeRepresentation.exchange_rate && (
        <div className="text-xs text-neutral-500">
          Converted from {activeRepresentation.calculated_from_currency} at rate {activeRepresentation.exchange_rate}
        </div>
      )}
    </div>
  )
}
